import { computed, inject, Injectable } from '@angular/core';
import { SiLocaleService } from '@siemens/element-ng/localization';
import { toSignal } from '@angular/core/rxjs-interop';
import { AVAILABLE_LOCALES, AvailableLocale } from '../config/locale.config';

/**
 * Locale information for display in the language switcher.
 */
export interface LocaleInfo {
    readonly code: AvailableLocale;
    readonly translationKey: string;
}

/**
 * Service for managing application localization.
 * Wraps SiLocaleService to provide a clean API for locale management.
 *
 * Usage:
 * ```typescript
 * private readonly localeService = inject(LocaleService);
 *
 * // Get current locale as a signal
 * currentLocale = this.localeService.currentLocale;
 *
 * // Change locale (triggers page reload by default)
 * this.localeService.setLocale('de');
 *
 * // Get available locales for a language switcher
 * availableLocales = this.localeService.availableLocales;
 * ```
 */
@Injectable({
    providedIn: 'root'
})
export class LocaleService {
    private readonly siLocaleService = inject(SiLocaleService);

    /**
     * Available locales with their translation keys for display.
     */
    public readonly availableLocales: readonly LocaleInfo[] = AVAILABLE_LOCALES.map(code => ({
        code,
        translationKey: `header.language.${this.getLanguageName(code)}`
    }));

    /**
     * The current locale as a signal.
     * Emits whenever the locale changes.
     */
    public readonly currentLocale = toSignal(this.siLocaleService.locale$, {
        initialValue: this.siLocaleService.locale
    });

    /**
     * Computed signal indicating if locale package is loaded.
     */
    public readonly isLocaleReady = computed(() => !!this.currentLocale());

    /**
     * Sets the application locale.
     * By default, this triggers a page reload to ensure all pipes and formatters
     * use the new locale correctly.
     *
     * @param locale - The locale code to set (e.g., 'en', 'de')
     */
    public setLocale(locale: AvailableLocale): void {
        if (!this.isValidLocale(locale)) {
            console.error(`Invalid locale: ${locale}. Available locales: ${AVAILABLE_LOCALES.join(', ')}`);
            return;
        }

        this.siLocaleService.locale = locale;
    }

    /**
     * Checks if the given locale is valid (part of available locales).
     *
     * @param locale - The locale code to check
     * @returns true if the locale is available
     */
    public isValidLocale(locale: string): locale is AvailableLocale {
        return this.siLocaleService.hasLocale(locale);
    }

    /**
     * Gets the language name key for a locale code.
     * Used for translation key generation.
     */
    private getLanguageName(code: AvailableLocale): string {
        const languageNames: Record<AvailableLocale, string> = {
            en: 'english',
            de: 'german'
        };
        return languageNames[code];
    }
}
